/**
 * Centralised axios instance.
 * - Base URL: `${REACT_APP_BACKEND_URL}/api`
 * - Auto-injects `Authorization: Bearer <access>` from localStorage
 * - On 401, attempts a one-shot refresh-token rotation and replays the request
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const STORAGE_KEYS = {
  access: "dealalert_access",
  refresh: "dealalert_refresh",
  user: "dealalert_user",
};

export const apiClient = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_KEYS.access);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshInFlight = null;

async function rotateRefresh() {
  const rt = localStorage.getItem(STORAGE_KEYS.refresh);
  if (!rt) return null;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = axios
    .post(`${BACKEND_URL}/api/auth/refresh`, { refresh_token: rt })
    .then((r) => {
      localStorage.setItem(STORAGE_KEYS.access, r.data.access_token);
      localStorage.setItem(STORAGE_KEYS.refresh, r.data.refresh_token);
      return r.data.access_token;
    })
    .catch(() => {
      localStorage.removeItem(STORAGE_KEYS.access);
      localStorage.removeItem(STORAGE_KEYS.refresh);
      localStorage.removeItem(STORAGE_KEYS.user);
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/**
 * P0.1 — direct token accessor for callers that bypass axios
 * (e.g. SSE via `fetch`). Reads the SAME source of truth as the
 * axios request interceptor: localStorage under STORAGE_KEYS.access.
 * NEVER read `apiClient.defaults.headers` — the token is never
 * stored there (interceptor sets it per-request).
 */
export function getAccessToken() {
  return localStorage.getItem(STORAGE_KEYS.access);
}

/**
 * P0.1 — single-flight refresh accessor for non-axios callers.
 * Shares the same `refreshInFlight` promise as the axios 401
 * interceptor so a concurrent axios refresh and an SSE-triggered
 * refresh never rotate twice. Returns the new access token, or
 * `null` if the refresh failed (caller MUST surface auth error —
 * do NOT silently fall back).
 */
export function refreshAccessToken() {
  return rotateRefresh();
}

apiClient.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    // Phase H2-followup — global EMAIL_NOT_VERIFIED gate. Any protected
    // route returning 403 + this code → bounce the user to the
    // /verify-pending screen instead of letting React Query render a
    // blank red error. We do NOT clear tokens (the user is still
    // authenticated, just not verified).
    if (
      status === 403 &&
      error.response?.data?.error?.code === "EMAIL_NOT_VERIFIED"
    ) {
      // Avoid an infinite loop when /verify-pending itself triggers a
      // protected call — check current path first.
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith("/verify-pending") &&
        !window.location.pathname.startsWith("/verify-email") &&
        !window.location.pathname.startsWith("/verify-success") &&
        // P1 — do NOT hard-bounce off the registration screen: a freshly
        // registered (still-unverified) user may hold a referral welcome
        // overlay here; a full-page assign would destroy it. RegisterPage
        // navigates to /verify-pending itself once the overlay is closed.
        !window.location.pathname.startsWith("/register")
      ) {
        window.location.assign("/verify-pending");
      }
      return Promise.reject(error);
    }
    if (status === 401 && !original._retry) {
      const errCode = error.response?.data?.error?.code;
      if (errCode === "token_expired" || errCode === "unauthorized" || errCode === "invalid_token") {
        original._retry = true;
        const fresh = await rotateRefresh();
        if (fresh) {
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${fresh}` };
          return apiClient.request(original);
        }
      }
    }
    return Promise.reject(error);
  },
);

export function formatApiError(error) {
  const detail = error?.response?.data;
  if (!detail) return error?.message || "Network error";
  if (detail.error && typeof detail.error.message === "string") return detail.error.message;
  if (typeof detail.detail === "string") return detail.detail;
  if (Array.isArray(detail.detail)) {
    return detail.detail.map((e) => e?.msg || JSON.stringify(e)).join(" • ");
  }
  return JSON.stringify(detail);
}
